/**
 * Analytics (read-only) data layer — the ONLY place this feature talks to the server. Every path +
 * capability is code-verified against server/src/modules/analytics/{routes,controller}.js. Server data
 * lives only in TanStack Query.
 *
 * Endpoints consumed (all under /api/v1, both gated analytics.tableau.view):
 *   GET   /analytics/tableau        — the dashboard read (live_today + history + referral|null).
 *                                     AUDIT-ON-READ fires server-side on every read (AN — audited).
 *   POST  /analytics/snapshot       — deterministic snapshot trigger (kept for the E2E gate; the
 *                                     dashboard is read-only and does not surface a write action).
 *
 * Cloud-degrade. analytics is HYBRID (mounts local), so /analytics/tableau is present on a local box.
 * But the prompt's "not available on this install" posture (a module unmounted → its routes 404) is
 * handled generically here: a NOT_FOUND/501 ApiError is surfaced as `isModuleAbsent`, never retried
 * (queryClient treats 404/501 as terminal), so the screen can render the clean unavailable state.
 */
import { ApiError, api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import { ANALYTICS_CAPS } from "./caps";
import { downloadExport } from "./exportLib";
import type { DashboardData, ExportData, ExportFormat } from "./types";

export { ANALYTICS_CAPS } from "./caps";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the analytics feature. */
export const analyticsKeys = {
  dashboard: (labId: string | null) => qk.scoped(labId, "analytics", "tableau"),
};

/**
 * The dashboard read. enabled is the gate: only fire when the caller has the capability AND is online
 * (the screen short-circuits the offline/forbidden states before the data branches). staleTime is
 * generous — the live current-day slice refreshes on focus-less remount, the snapshot history is daily.
 */
export function useDashboard(labId: string | null, enabled: boolean) {
  return useApiQuery<DashboardData>(analyticsKeys.dashboard(labId), "/analytics/tableau", {
    enabled,
    staleTime: 60_000,
  });
}

/**
 * NOVA-038 — the aggregate export download. The fetch hits GET /analytics/export (audited-on-read +
 * band-scoped server-side to the actor); the response is the aggregate row set, which the client renders
 * to the chosen format (JSON/CSV/XLSX) and downloads. Modelled as a mutation (an explicit user action with
 * a side-effect — the file write — not a cached read). No query invalidation: the export reads nothing the
 * dashboard caches.
 */
export function useExportDownload() {
  return useApiMutation<void, ExportFormat>(async (format) => {
    const data = await api.get<ExportData>("/analytics/export");
    downloadExport(data, format);
  });
}

/**
 * True when an ApiError means the module's route is not mounted on this install (cloud-only module on a
 * local box → 404, or a not-yet-implemented surface → 501). Drives the "not available here" state.
 */
export function isModuleAbsent(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !error.isForbidden &&
    !error.isUnauthenticated &&
    (error.httpStatus === 404 || error.httpStatus === 501 || error.code === "NOT_FOUND")
  );
}

/** Re-export for nav/screen gating without a second import. */
export const CAP = ANALYTICS_CAPS;
